/**
 * Customer Pricing Quote Form - Dynamic Decision Tree
 * Displays service questions and calculates real-time pricing estimates
 * Supports all 9 landscaping services (138-146) + 4 electrical (134-137)
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertCircle, CheckCircle2, ChevronRight, ChevronLeft } from 'lucide-react';
import apiService from '@/services/api';

const SERVICE_MAP = {
  134: { name: 'CCTV Installation', category: 'Electrical' },
  135: { name: 'Consumer Unit Replacement', category: 'Electrical' },
  136: { name: 'EICR Inspection', category: 'Electrical' },
  137: { name: 'Electrical Installation', category: 'Electrical' },
  138: { name: 'Decking', category: 'Landscaping' },
  139: { name: 'Fencing', category: 'Landscaping' },
  140: { name: 'Garden Clearance', category: 'Landscaping' },
  141: { name: 'Garden Maintenance', category: 'Landscaping' },
  142: { name: 'Hedge Cutting', category: 'Landscaping' },
  143: { name: 'Landscaping', category: 'Landscaping' },
  144: { name: 'Pressure Washing', category: 'Landscaping' },
  145: { name: 'Tree Surgery', category: 'Landscaping' },
  146: { name: 'Turfing', category: 'Landscaping' },
};

interface QuotingNode {
  id: number;
  node_number: number;
  node_title: string;
  node_question: string;
  is_entry_node: boolean;
  estimate_type_default: string;
}

interface QuotingOption {
  id: number;
  node_id: number;
  option_key: string;
  option_label: string;
  work_type_multiplier: number;
  complexity_multiplier: number;
  access_multiplier: number;
  occupation_multiplier: number;
  priority_multiplier: number;
  add_cost_gbp: number;
  estimate_type_override?: string;
  confidence_adjustment: number;
}

interface EstimateResponse {
  success: boolean;
  data: {
    base_cost: number;
    multipliers: Record<string, number>;
    total_cost: number;
    estimate_type: string;
    confidence_score: number;
  };
}

interface CustomerPricingQuoteFormProps {
  serviceId: number;
  onQuoteSubmit?: (quoteData: any) => void;
}

export default function CustomerPricingQuoteForm({ serviceId, onQuoteSubmit }: CustomerPricingQuoteFormProps) {
  const [currentNodeIndex, setCurrentNodeIndex] = useState(0);
  const [nodes, setNodes] = useState<QuotingNode[]>([]);
  const [options, setOptions] = useState<Record<number, QuotingOption[]>>({});
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, QuotingOption>>({});
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [estimate, setEstimate] = useState<any>(null);
  const [showEstimate, setShowEstimate] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const serviceName = SERVICE_MAP[serviceId as keyof typeof SERVICE_MAP]?.name || `Service ${serviceId}`;

  // Load decision tree questions
  useEffect(() => {
    const loadQuestions = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await apiService.request(
          `/api/pricing/electrical/${serviceId}/questions`,
          'GET'
        );

        if (response.success && response.data) {
          setNodes(response.data);
          // Load options for each node
          const optionsMap: Record<number, QuotingOption[]> = {};
          for (const node of response.data) {
            try {
              const optResponse = await apiService.request(
                `/api/pricing/electrical/${node.id}/options`,
                'GET'
              );
              if (optResponse.success) {
                optionsMap[node.id] = optResponse.data;
              }
            } catch (err) {
              console.error(`Failed to load options for node ${node.id}:`, err);
            }
          }
          setOptions(optionsMap);
        }
      } catch (err) {
        console.error('Error loading questions:', err);
        setError('Failed to load service questions. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadQuestions();
  }, [serviceId]);

  // Calculate estimate when answers change
  const calculateEstimate = async () => {
    try {
      setCalculating(true);
      const selectedOptions = Object.values(selectedAnswers).map(opt => opt.option_key);
      
      const response = await apiService.request(
        `/api/pricing/electrical/${serviceId}/estimate`,
        'POST',
        {
          selected_options: selectedOptions
        }
      );

      if (response.success) {
        setEstimate(response.data);
      }
    } catch (err) {
      console.error('Error calculating estimate:', err);
      setError('Failed to calculate estimate');
    } finally {
      setCalculating(false);
    }
  };

  const handleSelectOption = (node: QuotingNode, option: QuotingOption) => {
    const newAnswers = { ...selectedAnswers };
    newAnswers[node.id] = option;
    setSelectedAnswers(newAnswers);

    // Move to next node if this is the last one
    if (currentNodeIndex < nodes.length - 1) {
      setCurrentNodeIndex(currentNodeIndex + 1);
    } else {
      // All questions answered, calculate estimate
      setTimeout(() => calculateEstimate(), 300);
      setShowEstimate(true);
    }
  };

  const handleNext = () => {
    if (currentNodeIndex < nodes.length - 1) {
      setCurrentNodeIndex(currentNodeIndex + 1);
    } else {
      calculateEstimate();
      setShowEstimate(true);
    }
  };

  const handlePrevious = () => {
    if (currentNodeIndex > 0) {
      setCurrentNodeIndex(currentNodeIndex - 1);
    }
  };

  const handleSubmitQuote = async () => {
    if (!estimate) return;

    try {
      setSubmitting(true);
      
      // Create booking with quote data
      const bookingData = {
        service_id: serviceId,
        service_name: serviceName,
        selected_options: Object.keys(selectedAnswers).map(
          nodeId => selectedAnswers[parseInt(nodeId)].option_label
        ),
        estimated_cost: estimate.total_cost,
        estimate_type: estimate.estimate_type,
        confidence_score: estimate.confidence_score,
      };

      if (onQuoteSubmit) {
        onQuoteSubmit(bookingData);
      }
    } catch (err) {
      console.error('Error submitting quote:', err);
      setError('Failed to submit quote');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Card className="w-full">
        <CardContent className="flex items-center justify-center py-12">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
            <p className="text-gray-600">Loading {serviceName}...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const currentNode = nodes[currentNodeIndex];
  const nodeOptions = currentNode ? options[currentNode.id] || [] : [];
  const isAnswered = currentNode && selectedAnswers[currentNode.id];
  const allAnswered = nodes.length > 0 && nodes.every(node => selectedAnswers[node.id]);

  return (
    <div className="w-full max-w-2xl mx-auto space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{serviceName} Quote</CardTitle>
          <CardDescription>
            Answer {currentNodeIndex + 1} of {nodes.length} questions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {showEstimate && estimate ? (
            <div className="space-y-4">
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800 font-medium">
                  Your quote is ready!
                </AlertDescription>
              </Alert>

              <div className="grid grid-cols-1 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-sm text-gray-600 font-medium mb-1">Estimated Cost</p>
                  <p className="text-3xl font-bold text-blue-600">
                    ${estimate.total_cost?.toFixed(2) || '0.00'}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                    <p className="text-xs text-gray-600 font-medium mb-1">Estimate Type</p>
                    <p className="text-lg font-bold text-purple-600">
                      {estimate.estimate_type === 'FIXED' && 'Fixed Price'}
                      {estimate.estimate_type === 'RANGE' && 'Price Range'}
                      {estimate.estimate_type === 'ASSESSMENT_REQUIRED' && 'Assessment Needed'}
                    </p>
                  </div>

                  <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                    <p className="text-xs text-gray-600 font-medium mb-1">Confidence</p>
                    <p className="text-lg font-bold text-amber-600">
                      {estimate.confidence_score}%
                    </p>
                  </div>
                </div>

                {estimate.estimate_type === 'ASSESSMENT_REQUIRED' && (
                  <Alert className="border-amber-200 bg-amber-50">
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-amber-800 text-sm">
                      This quote requires a site assessment to finalize. A provider will contact you within 24 hours.
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              <div className="pt-4 border-t space-y-2">
                <Button
                  onClick={handleSubmitQuote}
                  disabled={submitting}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Submit Quote
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => {
                    setShowEstimate(false);
                    setCurrentNodeIndex(0);
                    setSelectedAnswers({});
                    setEstimate(null);
                  }}
                  variant="outline"
                  className="w-full"
                >
                  Start Over
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {currentNode && (
                <>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">{currentNode.node_title}</h3>
                    <p className="text-gray-600">{currentNode.node_question}</p>
                  </div>

                  <RadioGroup>
                    <div className="space-y-3">
                      {nodeOptions.map((option) => (
                        <div
                          key={option.id}
                          className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                          onClick={() => handleSelectOption(currentNode, option)}
                        >
                          <RadioGroupItem
                            value={option.id.toString()}
                            id={option.id.toString()}
                            checked={isAnswered && selectedAnswers[currentNode.id]?.id === option.id}
                          />
                          <Label htmlFor={option.id.toString()} className="flex-1 cursor-pointer">
                            <span className="font-medium">{option.option_label}</span>
                          </Label>
                        </div>
                      ))}
                    </div>
                  </RadioGroup>

                  <div className="flex justify-between gap-4 pt-4 border-t">
                    <Button
                      onClick={handlePrevious}
                      disabled={currentNodeIndex === 0}
                      variant="outline"
                    >
                      <ChevronLeft className="h-4 w-4 mr-2" />
                      Previous
                    </Button>
                    <Button
                      onClick={handleNext}
                      disabled={!isAnswered || calculating}
                    >
                      {calculating ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Calculating...
                        </>
                      ) : currentNodeIndex === nodes.length - 1 ? (
                        <>
                          Calculate Quote
                          <ChevronRight className="h-4 w-4 ml-2" />
                        </>
                      ) : (
                        <>
                          Next
                          <ChevronRight className="h-4 w-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </div>

                  <div className="flex gap-1 mt-4">
                    {nodes.map((_, index) => (
                      <div
                        key={index}
                        className={`h-2 flex-1 rounded-full ${
                          index <= currentNodeIndex ? 'bg-blue-600' : 'bg-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
